/**
 * Shared `matchMedia` breakpoint plumbing for GTCC.
 *
 * Both the Mono-Deck nav collapse (`GtccShell.tsx`) and the Ask Genius
 * Tile grid (`AskGeniusSummary.tsx`) key off the same
 * `--gt-cmp-gtcc-nav-collapse-bp` design token so the nav and the Tile
 * grid change layout at the same viewport width instead of drifting
 * independently (audit finding G3-6).
 */
import { useEffect, useState } from "react";

/** Falls back to the token's own CSS default when it isn't computable (SSR, jsdom). */
const BREAKPOINT_FALLBACK_PX = "1024px";
export const GTCC_BREAKPOINT_TOKEN = "--gt-cmp-gtcc-nav-collapse-bp";

export function canMatchMedia(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function resolveBreakpointPx(): string {
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
    return BREAKPOINT_FALLBACK_PX;
  }
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(GTCC_BREAKPOINT_TOKEN)
    .trim();
  return value || BREAKPOINT_FALLBACK_PX;
}

/** `max-width` query — true below the breakpoint (used to collapse the nav). */
export function resolveMaxWidthBreakpointQuery(): string {
  return `(max-width: ${resolveBreakpointPx()})`;
}

/** `min-width` query — true at/above the breakpoint (used to widen the Tile grid). */
export function resolveMinWidthBreakpointQuery(): string {
  return `(min-width: ${resolveBreakpointPx()})`;
}

function matchesNow(query: string): boolean {
  return canMatchMedia() ? window.matchMedia(query).matches : false;
}

/** Synchronous, one-off read — use for computing an initial `useState` value. */
export function isMaxWidthBreakpointNow(): boolean {
  return matchesNow(resolveMaxWidthBreakpointQuery());
}

/** Subscribes to a `resolveQuery()`-produced query, re-evaluated once on mount. */
export function useBreakpointMatch(resolveQuery: () => string): boolean {
  const [matches, setMatches] = useState(() => matchesNow(resolveQuery()));

  useEffect(() => {
    if (!canMatchMedia()) return;
    const mql = window.matchMedia(resolveQuery());
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolveQuery is a stable module-level function per call site
  }, []);

  return matches;
}
